/**
 * The boot-time switch between the current UI and the Foresight New UI (the MUI reskin).
 *
 * Decided once, before any theme module evaluates: the theme is module-level constants that
 * hundreds of `css` blocks read at import time, so a React hook could not swap it. `?newUi=1`
 * (or `?useNewUI=1`) turns the new UI on and `?newUi=0` off; the choice persists in localStorage
 * across reloads. Only the internal environments honour it: a client could otherwise be handed
 * a link that reskins their site and stays that way in their browser.
 */
const STORAGE_KEY = 'fd-new-ui';

/** The first hostname label of each environment the switch works on; localhost covers dev servers and tests. */
const ALLOWED_HOSTS = ['localhost', 'dev', 'purple', 'demo'];

function isAllowedHost(): boolean {
  const [first] = window.location.hostname.split('.');
  return ALLOWED_HOSTS.includes(first) || window.location.hostname === '127.0.0.1';
}

/** `1` / `true` turns the New UI on, `0` / `false` off; anything else leaves the stored choice alone. */
function parseSwitch(value: string | null): '1' | '0' | null {
  const normalised = value?.trim().toLowerCase();
  if (normalised === '1' || normalised === 'true') {
    return '1';
  }
  if (normalised === '0' || normalised === 'false') {
    return '0';
  }
  return null;
}

function readNewUi(): boolean {
  try {
    if (!isAllowedHost()) {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    const key = [...params.keys()].find((k) => k.toLowerCase() === 'newui' || k.toLowerCase() === 'usenewui');
    const choice = key === undefined ? null : parseSwitch(params.get(key));
    if (choice !== null) {
      window.localStorage.setItem(STORAGE_KEY, choice);
    }
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export const isNewUi = readNewUi();

/** The font stacks the switch decides; every font-family outside the MUI theme reads these. */
export const fonts = isNewUi
  ? {
      base: "'Source Sans 3', sans-serif",
      heading: "'DM Sans', sans-serif",
      mono: "'Source Sans 3', sans-serif",
    }
  : {
      base: 'Wotfard-Regular',
      heading: 'Wotfard-Regular',
      mono: 'IBMPlexSans-Regular',
    };

/** One corner radius for every rounded component the theme does not reach. */
export const radius = isNewUi ? '6px' : '4px';
